import axiosInstance from './axiosInstance';

const PRODUCER_BOARD_COLORS = ['#E94934', '#FFD92B', '#FFA40D', '#3B82F6'];
const PRODUCER_BOARD_ICONS = ['📚', '🥊', '✨', '🎬'];
const TALENT_DEFAULT_BOARD_EMOJI = '💛';
const TALENT_DEFAULT_BOARD_COLOR = '#FFF9E6';
const GENERIC_BOARD_EMOJI = '📁';
const GENERIC_BOARD_COLOR = '#F5F5F5';

/** GET /api/archive/boards → [{ id, name, isDefault, itemCount, createdAt }] */
export const getBoards = async () => {
  const res = await axiosInstance.get('/api/archive/boards');
  // 배열 직접 반환 또는 { boards: [...] } 래핑 모두 대응
  return Array.isArray(res.data) ? res.data : (res.data?.boards ?? []);
};

/** POST /api/archive/boards — body: { name, color?, emoji?, description? } */
export const createBoard = async (data) => {
  const res = await axiosInstance.post('/api/archive/boards', data);
  return res.data;
};

/** PATCH /api/archive/boards/{boardId} — body: { name?, color?, emoji?, description? } */
export const updateBoard = async (id, data) => {
  const res = await axiosInstance.patch(`/api/archive/boards/${id}`, data);
  return res.data;
};

/** DELETE /api/archive/boards/{boardId} */
export const deleteBoard = async (id) => {
  await axiosInstance.delete(`/api/archive/boards/${id}`);
};

/**
 * GET /api/archive/boards/{boardId}/items
 * TALENT_PROFILE 아이템이면 talentProfile 임베드 포함
 */
export const getBoardItems = async (boardId) => {
  const res = await axiosInstance.get(`/api/archive/boards/${boardId}/items`);
  return Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
};

/** GET /api/archive/boards/items — 내 전체 아이템 (모든 보드 합산) */
export const getAllSavedItems = async () => {
  const res = await axiosInstance.get('/api/archive/boards/items');
  return Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
};

/**
 * POST /api/archive/boards/items → 201 { id }
 * @param {{ boardId: number, targetType: "CASTING_POST"|"TALENT_PROFILE", targetId: number }} data
 */
export const addBoardItem = async (data) => {
  const res = await axiosInstance.post('/api/archive/boards/items', data);
  return res.data;
};

/** DELETE /api/archive/boards/items/{itemId} */
export const removeBoardItem = async (itemId) => {
  await axiosInstance.delete(`/api/archive/boards/items/${itemId}`);
};

/** PATCH /api/archive/boards/items/{itemId}/move — body: { boardId } */
export const moveBoardItem = async (itemId, boardId) => {
  const res = await axiosInstance.patch(`/api/archive/boards/items/${itemId}/move`, { boardId });
  return res.data;
};

const BOARD_BG_BY_COLOR = {
  '#E94934': '#FDECEA',
  '#FFD92B': '#FFF8D5',
  '#FFA40D': '#FFF3E0',
  '#007AD2': '#EBF3FF',
  '#3B82F6': '#EBF3FF',
};

function clampChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function getBoardAccentBackground(color, opacity = 0.14) {
  if (!color || typeof color !== 'string') return `rgba(245, 245, 245, ${opacity})`;
  if (BOARD_BG_BY_COLOR[color]) return BOARD_BG_BY_COLOR[color];
  const hex = color.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map((char) => char + char).join('')
    : hex;
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return `rgba(245, 245, 245, ${opacity})`;
  const r = clampChannel(parseInt(normalized.slice(0, 2), 16));
  const g = clampChannel(parseInt(normalized.slice(2, 4), 16));
  const b = clampChannel(parseInt(normalized.slice(4, 6), 16));
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}


/**
 * board 목록에 accentBackground를 병합 (API 값 우선, fallback 지원)
 * @param {Array} boards
 * @returns {Array}
 */
export function mergeBoardMeta(boards) {
  return boards.map((b) => ({
    ...b,
    accentBackground: b.accentBackground ?? getBoardAccentBackground(b.color),
  }));
}

function getBoardItemTargetId(item, targetType) {
  if (targetType === 'TALENT_PROFILE') {
    return (
      item?.targetId ??
      item?.talentProfile?.id ??
      item?.talentProfile?.talentProfileId ??
      item?.talent?.id ??
      item?.talent?.talentProfileId ??
      item?.id ??
      null
    );
  }
  if (targetType === 'CASTING_POST') {
    return item?.targetId ?? item?.castingPost?.id ?? item?.casting?.id ?? item?.target?.id ?? item?.id ?? null;
  }
  return item?.targetId ?? item?.id ?? null;
}

export function countBoardItemsByTarget(items, targetType) {
  const list = Array.isArray(items) ? items : (items?.content ?? []);
  return new Set(
    list
      .filter((item) => !item?.targetType || item.targetType === targetType)
      .map((item) => getBoardItemTargetId(item, targetType))
      .filter(Boolean)
      .map(String)
  ).size;
}

export function normalizeBoardForDisplay(board, { targetType = 'CASTING_POST', index = 0, preferredDefaultBoardId = null, count } = {}) {
  const isPreferredDefault = preferredDefaultBoardId != null && String(preferredDefaultBoardId) === String(board.id);
  const isDefault = preferredDefaultBoardId != null ? isPreferredDefault : Boolean(board.isDefault);
  const isTalentBoard = targetType === 'TALENT_PROFILE';

  const fallbackColor = isTalentBoard
    ? PRODUCER_BOARD_COLORS[index % PRODUCER_BOARD_COLORS.length]
    : (isDefault ? TALENT_DEFAULT_BOARD_COLOR : GENERIC_BOARD_COLOR);
  const fallbackEmoji = isTalentBoard
    ? PRODUCER_BOARD_ICONS[index % PRODUCER_BOARD_ICONS.length]
    : (isDefault ? TALENT_DEFAULT_BOARD_EMOJI : GENERIC_BOARD_EMOJI);
  const finalColor = board.color ?? fallbackColor;
  const finalEmoji = board.emoji ?? board.icon ?? fallbackEmoji;
  const finalName = board.name;
  const finalDesc = board.description ?? board.desc ?? '';
  const finalCount = count ?? board.count ?? board.itemCount ?? 0;

  return {
    ...board,
    name: finalName,
    desc: finalDesc,
    emoji: finalEmoji,
    color: finalColor,
    count: finalCount,
    isDefault,
    isServerDefault: board.isDefault ?? false,
    accentBackground: board.accentBackground ?? getBoardAccentBackground(finalColor),
    iconBackground: finalColor,
  };
}

export async function getBoardPickerOptions({ targetType = 'CASTING_POST', preferredDefaultBoardId = null } = {}) {
  const boards = await getBoards();
  const normalized = await Promise.all(
    (boards || []).map(async (board, index) => {
      let count = board.itemCount ?? 0;
      try {
        const items = await getBoardItems(board.id);
        count = countBoardItemsByTarget(items, targetType);
      } catch {}
      return normalizeBoardForDisplay(board, { targetType, index, preferredDefaultBoardId, count });
    })
  );

  return normalized.sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return 0;
  });
}

/**
 * 기본 보드 ID를 조회하거나 없으면 생성 후 반환
 * @returns {Promise<number>} boardId
 */
export const getOrCreateDefaultBoard = async () => {
  const boards = await getBoards();
  const defaultBoard =
    boards.find((b) => b.isDefault) ?? boards[0] ?? null;
  if (defaultBoard) return defaultBoard.id;
  const created = await createBoard({ name: '관심 목록' });
  return created.id;
};

/**
 * 기본 보드에 아이템 저장 (boardId 자동 주입)
 * @param {{ targetType: string, targetId: number }} data
 */
export const saveToDefaultBoard = async (data) => {
  const boardId = await getOrCreateDefaultBoard();
  return addBoardItem({ boardId, ...data });
};
